// Sidebar.js
import React, { useState, useEffect } from 'react';
import './sidebar.css';
import logo from '../Assets/logo.png';
import SideBarNavItem from './SideBarNavItem';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

function Sidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchTargetName, setSwitchTargetName] = useState('');

  // 🔽 collapse / expand width
  const [isCollapsed, setIsCollapsed] = useState(false);

  const {
    restaurants,
    primaryRestaurantId,
    setSelectedRestaurantId,
  } = useAuth();

  const isAuthRoute =
    location.pathname === '/login' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/view-as';

  // Drawer screens use a full-width off-canvas menu. Collapse only applies on desktop.
  useEffect(() => {
    if (isAuthRoute) return;

    const drawerQuery = window.matchMedia('(max-width: 1023px)');

    const syncWidth = () => {
      if (drawerQuery.matches) {
        setIsCollapsed(false);
        document.documentElement.style.setProperty('--sidebar-w', '0px');
        return;
      }
      document.documentElement.style.setProperty(
        '--sidebar-w',
        isCollapsed ? '60px' : '355px'
      );
    };

    syncWidth();
    drawerQuery.addEventListener('change', syncWidth);
    return () => drawerQuery.removeEventListener('change', syncWidth);
  }, [isAuthRoute, isCollapsed]);

  const toggleSidebar = () => {
    console.log('close');
    setIsSidebarOpen(!isSidebarOpen);
  };

  // 🔽 width toggle handler
  const toggleSidebarWidth = () => {
    setIsCollapsed((prev) => {
      const newState = !prev;

      if (newState) {
        document.documentElement.style.setProperty('--sidebar-w', '60px');
      } else {
        document.documentElement.style.setProperty('--sidebar-w', '355px');
      }

      return newState;
    });
  };

  const handleTenantChange = async (e) => {
    const newId = e.target.value;

    // Find selected restaurant record
    const r = restaurants?.find(
      (rest) => String(rest.id) === String(newId)
    );

    if (!r) {
      console.warn('No restaurant record for id', newId);
      return;
    }

    const displayName = `${r.restaurant} (${r.location})`;
    const serverUrl = (r.server || '').replace(/\/+$/, '');

    if (!serverUrl) {
      alert(`No server URL configured for ${displayName}.`);
      return;
    }

    setSwitchTargetName(displayName);
    setIsSwitching(true);

    try {
      const PING_PATH = '/tablemate/endpoints/ping.php';
      const testUrl = `${serverUrl}${PING_PATH}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout

      const res = await fetch(testUrl, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        throw new Error(`Ping failed with status ${res.status}`);
      }

      setSelectedRestaurantId(newId);
      navigate('/');
      setIsSwitching(false);
    } catch (err) {
      console.error('Tenant switch test connection failed:', err);
      alert(
        `Failed to connect to ${displayName} server. Please contact Table Mate supoprt to help diagnose issue.`
      );
      setIsSwitching(false);
    }
  };

  return (
    <>
      {/* Full-screen "Switching Restaurant" overlay */}
      {isSwitching && (
        <div className="tenant-switch-overlay">
          <div className="tenant-switch-dialog">
            <div className="tenant-switch-spinner" />
            <div className="tenant-switch-text">
              Switching restaurant to{' '}
              <strong>{switchTargetName}</strong>…
            </div>
          </div>
        </div>
      )}

      <div>
        <div
          className={`sidebar-content overlay ${
            isCollapsed ? 'sidebar-collapsed' : ''
          }${isOpen ? ' sidebar-drawer-open' : ''}`}
          style={{ display: isOpen ? 'block' : 'none' }}
        >
          <div className="sidebar-top" onClick={(e) => e.stopPropagation()}>
            <div className="tm-logo">
              <img src={logo} alt="Custom Logo" />
              <div className="header-info">
                <p className="company-name">Table Mate</p>
                <p className="site-title">Back End Server Application</p>
              </div>

              {/* 🔽 collapse/expand button */}
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={toggleSidebarWidth}
              >
                {isCollapsed ? '»' : '«'}
              </button>

              {/* 🔽 Tenant switcher just below tm-logo */}
              {restaurants && restaurants.length > 0 && (
                <div className="tenant-switcher">
                  <label htmlFor="tenant-select" className="tenant-label">
                    Switch Restaurant
                  </label>
                  <select
                    id="tenant-select"
                    style={{ maxWidth: '100%' }}
                    className="tenant-select"
                    value={primaryRestaurantId || ''}
                    onChange={handleTenantChange}
                    disabled={isSwitching}
                  >
                    {restaurants.map((r) => (
                      <option key={r.id} value={r.id}>
                        {`${r.restaurant} (${r.location})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="sidebar-nav" onClick={(e) => e.stopPropagation()}>
            <ul className="sidebar-links">
              <SideBarNavItem title="Home" code="home" icon="home" link="/" />
              <SideBarNavItem title="Menu" code="menu" icon="menu" link="/menu" />
              <SideBarNavItem title="Tables" code="tables" icon="tables" link="/tables" />
              <SideBarNavItem title="Data" code="data" icon="statistics" link="/data" />
              <SideBarNavItem title="Help" code="help" icon="help" link="/help" />
              <SideBarNavItem title="Users" code="users" icon="user" link="/users" />
              <SideBarNavItem title="Settings" code="settings" icon="settings" link="/settings" />
              <SideBarNavItem title="Sign Out" code="users" icon="signout" link="/signout" />
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
